import type { CountryCode } from 'libphonenumber-js';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { formatNationalByCountryCode, getExamplePhone } from '@/lib/phone';

interface UsePhoneInputOptions {
  prefixFieldName?: string;
  phoneFieldName?: string;
}

export const usePhoneInput = ({
  prefixFieldName = 'phonePrefix',
  phoneFieldName = 'phone',
}: UsePhoneInputOptions = {}) => {
  const tForms = useTranslations('forms');
  const { clearErrors, setError, setValue, watch } = useFormContext();
  const [phonePrefix, phone] = watch([prefixFieldName, phoneFieldName]);
  const [examplePhone, setExamplePhone] = useState<string>('');

  useEffect(() => {
    if (!phone || !phonePrefix) return;

    try {
      const phoneNumber = parsePhoneNumberWithError(phone, {
        defaultCountry: phonePrefix as CountryCode,
        extract: false,
      });

      const formatted = formatNationalByCountryCode(phonePrefix, phoneNumber.formatNational());
      if (formatted !== phone) {
        setValue(phoneFieldName, formatted);
      }

      if (phoneNumber.isValid()) {
        clearErrors(phoneFieldName);
      } else {
        setError(phoneFieldName, {
          message: tForms('invalidPhoneNumber'),
        });
      }
    } catch {
      setError(phoneFieldName, {
        message: tForms('invalidPhoneNumber'),
      });
    }
  }, [phone, phonePrefix, clearErrors, setError, setValue, phoneFieldName]);

  useEffect(() => {
    if (!phonePrefix) return;
    const example = getExamplePhone(phonePrefix as CountryCode);
    if (example) setExamplePhone(example);
  }, [phonePrefix, setExamplePhone]);

  return { examplePhone };
};
